<template>
	<StandardModal
		open
		medium
		:title="t('admin_integrations_role_mapping')"
		:confirmText="t('common_button_save')"
		:confirmProps="{ disabled: !canSave || isBlocked, loading: isSaving }"
		@confirm="onSave"
		@cancel="emit('cancel')"
		class="role-mapping-modal"
		ref="modal"
	>
		<div class="role-mapping-wrapper">
			<div class="role-mapping-description">
				{{ t('admin_integrations_role_mapping_desc') }}
			</div>

			<div class="role-mapping-headers">
				<div class="column-label name-column">
					{{ t('admin_integrations_external_role_name') }}
				</div>
				<div class="column-label role-column">
					{{ t('admin_integrations_maps_to') }}
				</div>
				<div class="remove-cell" />
			</div>

			<div class="role-mapping-row" v-for="(row, index) in rows" :key="row.id">
				<Input
					v-model="row.name"
					:placeholder="t('admin_integrations_role_name')"
					class="name-column"
					:class="{ 'form-item-error': duplicateRowIds.has(row.id) }"
				/>
				<Dropdown
					:options="roleOptions"
					v-model="row.role"
					global
					class="role-column"
					optionsClass="role-mapping-role-options"
				>
					<template #selected="{ option }">{{ $t(option.label) }}</template>
					<template v-slot="{ option }">{{ $t(option.label) }}</template>
				</Dropdown>
				<div class="remove-cell">
					<button
						type="button"
						class="remove-row"
						:title="t('common_menu_remove')"
						@click="removeRow(index)"
					>
						<TrashCanIcon type="regular" />
					</button>
				</div>
			</div>

			<div v-if="hasDuplicates" class="role-mapping-error">
				{{ t('admin_integrations_duplicate_role_name') }}
			</div>

			<Button link class="add-role-button" @click="addRow">
				<PlusIcon type="regular" />
				{{ t('admin_integrations_add_role') }}
			</Button>
		</div>
	</StandardModal>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { StandardModal, Input, Dropdown, Button } from '@/components/common'
import { PlusIcon, TrashCanIcon } from '@/icons'

const props = defineProps({
	roleMappings: {
		type: Object,
		required: false,
		default: null,
	},
	isSaving: {
		type: Boolean,
		default: false,
	},
	// Another write to the configure endpoint is in flight. Saving now would be
	// dropped by the parent's shared guard, so disable it rather than let the
	// click do nothing.
	isBlocked: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['save', 'cancel'])

const { t } = useI18n()

const modal = ref(null)

// Server matches the highest role first, so keep rows in that order
const ROLE_ORDER = ['owner', 'member', 'ignored']

const roleOptions = [
	{ id: 'member', label: 'model_role_member' },
	{ id: 'owner', label: 'model_role_owner' },
	{ id: 'ignored', label: 'admin_integrations_role_ignored' },
]

let rowId = 0
const createRow = (name = '', role = 'member') => ({ id: rowId++, name, role })

const initialRows = () => {
	const flattened = []
	ROLE_ORDER.forEach((role) => {
		const names = props.roleMappings?.[role] || []
		names.forEach((name) => flattened.push(createRow(name, role)))
	})
	if (!flattened.length) flattened.push(createRow())
	return flattened
}

const rows = ref(initialRows())

// All three keys are always sent so removed mappings are cleared server-side
const normalize = (rowList) => {
	const mappings = { owner: [], member: [], ignored: [] }
	rowList.forEach(({ name, role }) => {
		const trimmed = (name || '').trim()
		if (!trimmed || !mappings[role]) return
		mappings[role].push(trimmed)
	})
	return mappings
}

// The server matches an external role to a single Pronto role, so the same
// name can't appear twice. Flag the repeat rows and block saving rather than
// silently dropping them, which left Save disabled with no visible reason.
const duplicateRowIds = computed(() => {
	const seen = new Set()
	const duplicates = new Set()
	rows.value.forEach(({ id, name }) => {
		const trimmed = (name || '').trim()
		if (!trimmed) return
		if (seen.has(trimmed)) duplicates.add(id)
		else seen.add(trimmed)
	})
	return duplicates
})

const hasDuplicates = computed(() => duplicateRowIds.value.size > 0)

const initialSnapshot = JSON.stringify(normalize(rows.value))
const canSave = computed(
	() =>
		!hasDuplicates.value &&
		JSON.stringify(normalize(rows.value)) !== initialSnapshot
)

const addRow = () => rows.value.push(createRow())
const removeRow = (index) => {
	rows.value.splice(index, 1)
	// never leave the modal empty — removing the last row resets it
	if (!rows.value.length) rows.value.push(createRow())
}
const onSave = () => emit('save', normalize(rows.value))

// Lets ConnectionDetails report a failed save through this modal's own alert,
// since the modal stays open when saving fails
defineExpose({ modal })
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.role-mapping-wrapper {
	padding: 24px;

	.role-mapping-description {
		font-size: 16px;
		font-family: Avenir-Roman;
		color: var(--neutral-600);
		margin-bottom: 24px;
	}

	.role-mapping-headers {
		display: flex;
		align-items: center;
		gap: 14px;
		margin-bottom: 8px;

		.column-label {
			font-size: 13px;
			text-transform: uppercase;
			letter-spacing: 0.5px;
			font-family: Avenir-Roman;
			color: var(--neutral-600);
		}
	}

	.role-mapping-row {
		display: flex;
		align-items: center;
		gap: 14px;
		margin-bottom: 14px;
	}

	.name-column {
		flex: 1;
		min-width: 0;
	}

	.role-column {
		width: 140px;
		flex-shrink: 0;
	}

	.remove-cell {
		width: 28px;
		flex-shrink: 0;

		.remove-row {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 28px;
			height: 28px;
			padding: 0;
			border: 0;
			border-radius: 6px;
			background: rgba(224, 100, 119, 0.1);
			color: var(--red-200);
			cursor: pointer;

			&:hover {
				color: var(--red-300);
			}

			.pronto-icon {
				font-size: 13px;
			}
		}
	}

	.role-mapping-error {
		font-family: Avenir-Roman;
		font-size: 13px;
		color: var(--red-200);
		margin-bottom: 14px;
	}

	.add-role-button {
		padding: 0;
		gap: 6px;

		.pronto-icon {
			font-size: 12px;
		}
	}
}
</style>

<style lang="scss">
.dropdown-options-container.role-mapping-role-options {
	width: 140px !important;
}
</style>
